<template>
  <TriadPullToRefresh :refreshing="isRefreshing" @refresh="handleRefresh">
    <div class="relative h-full w-full max-w-full overflow-x-hidden min-w-0">
      <div class="hidden sm:flex absolute max-w-[85px] top-0 left-1/2 transform -translate-x-1/2 z-20 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg gap-1 shadow-xs">
          <button 
              @click="switchView('list')"
              class="p-2 rounded-md transition-all"
              :class="view === 'list' ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'"
          >
              <ListBulletIcon class="w-5 h-5" />
          </button>
          <button 
              @click="switchView('dashboard')"
              class="p-2 rounded-md transition-all"
              :class="view === 'dashboard' ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'"
          >
              <Squares2X2Icon class="w-5 h-5" />
          </button>
      </div>

      <transition name="slide-left" mode="out-in" :duration="{ enter: 200, leave: 150 }">
        <ListPlaylist 
          v-if="activeView === 'list'" 
          :playlists="playlists" 
          :loading="loading" 
          :hasMore="hasMore"
          :loadingMore="loadingMore"
          :favoriteIds="favoriteIds"
          @load-more="loadMorePlaylists"
        />
        <DashboardPlaylist 
          v-else-if="activeView === 'dashboard'" 
          :playlists="playlists" 
          :status="status"
          :hasMore="hasMore"
          :loadingMore="loadingMore"
          :favoriteIds="favoriteIds"
          @load-more="loadMorePlaylists"
        />
      </transition>
    </div>
  </TriadPullToRefresh>
</template>
<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import ListPlaylist from '@/components/playlist/ListPlaylist.vue';
import PlaylistService from '@/service/playlist.service';
import DashboardPlaylist from '@/components/playlist/DashboardPlaylist.vue';
import TriadPullToRefresh from '@/components/common/elements/TriadPullToRefresh.vue';
import { ListBulletIcon, Squares2X2Icon } from '@heroicons/vue/24/outline';
import userStore from '@/store/user.store';
import toastStore from '@/store/toast.store';
import { appEvents } from '@/store/app.events';

const { t } = useI18n();
const isRefreshing = ref(false);

const playlists = ref([])
const loading = ref(true);
const status = ref('loading');
const page = ref(1);
const size = 10;
const hasMore = ref(false);
const loadingMore = ref(false);
const favoriteIds = ref(new Set());

const savedView = localStorage.getItem('playlist_view_preference');
const initialView = savedView || 'dashboard';

const view = ref(initialView);
const activeView = ref(initialView);

function switchView(newView) {
  view.value = null; // Start exit transition
  localStorage.setItem('playlist_view_preference', newView);
  setTimeout(() => {
    activeView.value = newView;
    view.value = newView; // Start enter transition
  }, 150); // Match your leave duration
}

const getPlaylistKeys = (p) => {
    if (!p) return [];
    const keys = [];
    if (p._id) keys.push(String(p._id));
    if (p.id) keys.push(String(p.id));
    if (p.uuid) keys.push(String(p.uuid));
    return keys;
};

const arePlaylistsEqual = (p1, p2) => {
    if (!p1 || !p2) return false;
    const k1 = getPlaylistKeys(p1);
    const k2 = getPlaylistKeys(p2);
    if (k1.some(k => k2.includes(k))) return true;
    if (p1.name && p2.name && p1.name.trim().toLowerCase() === p2.name.trim().toLowerCase()) return true;
    return false;
};

const deduplicatePlaylists = (playlistArray) => {
    const result = [];
    (playlistArray || []).forEach(p => {
        if (!p) return;
        const exists = result.some(item => arePlaylistsEqual(item, p));
        if (!exists) {
            result.push(p);
        }
    });
    return result;
};

const favoritePlaylists = ref([]);

const fetchFavoritesData = async () => {
  try {
    const favorites = await PlaylistService.fetchFavorites();    favoritePlaylists.value = favorites || [];
    const favSet = new Set();
    favoritePlaylists.value.forEach(p => {
      getPlaylistKeys(p).forEach(k => favSet.add(k));
    });
    favoriteIds.value = favSet;
  } catch (error) {
    if (error && error.code === 'UNAUTHORIZED') {
      // Handler global redireciona ao login; não marca tela como vazia.
      throw error;
    }
    console.error("Failed to fetch favorites:", error);
  }
}

const isFavoritePlaylist = (p) => {
  if (!p) return false;
  if (p.isFavorite) return true;
  const keys = getPlaylistKeys(p);
  if (keys.some(k => favoriteIds.value.has(k))) return true;
  if (p.name && favoritePlaylists.value.some(fav => fav.name && fav.name.trim().toLowerCase() === p.name.trim().toLowerCase())) return true;
  return false;
};

const applyCachedPlaylists = () => {
  const currentUserId = userStore.state.userInfo?.id || userStore.state.userInfo?._id || PlaylistService.getCurrentUserId();
  if (!currentUserId) {
    return false;
  }
  const cachedFavs = PlaylistService.getCachedFavorites(currentUserId);
  const cachedPag = PlaylistService.getCachedPaginated(1, '', currentUserId);
  if ((cachedFavs && cachedFavs.length > 0) || (cachedPag && cachedPag.data && cachedPag.data.length > 0)) {
    if (Array.isArray(cachedFavs)) {
      favoritePlaylists.value = cachedFavs;
      const favSet = new Set();
      cachedFavs.forEach(p => {
        getPlaylistKeys(p).forEach(k => favSet.add(k));
      });
      favoriteIds.value = favSet;
    }
    const cachedData = cachedPag?.data || [];
    cachedData.forEach(p => {
      p.isFavorite = isFavoritePlaylist(p);
    });
    favoritePlaylists.value.forEach(p => {
      p.isFavorite = true;
    });
    const merged = deduplicatePlaylists([...favoritePlaylists.value, ...cachedData]);
    if (merged.length > 0) {
      playlists.value = merged;
      status.value = 'loaded';
      loading.value = false;
      return true;
    }
  }
  return false;
};

const fetchPlaylists = async (forceBypassCache = false) => {
  const hadCachedData = !forceBypassCache && applyCachedPlaylists();
  if (!hadCachedData) {
    status.value = 'loading';
    loading.value = true;
  }
  page.value = 1;
  hasMore.value = false;
  try {
    // Parallelize with Promise.all: both requests run concurrently instead of sequentially
    const [, res] = await Promise.all([
      fetchFavoritesData(),
      PlaylistService.fetchPaginated({ page: page.value, size: size })
    ]);
    const fetchedData = res?.data || [];
    
    fetchedData.forEach(p => {
      p.isFavorite = isFavoritePlaylist(p);
    });

    favoritePlaylists.value.forEach(p => {
      p.isFavorite = true;
    });

    // Merge favorites with paginated results without duplicates
    playlists.value = deduplicatePlaylists([...favoritePlaylists.value, ...fetchedData]);
    
    const totalPages = res?.totalPages || 0;
    if (totalPages > 0) {
      hasMore.value = page.value < totalPages;
    } else {
      hasMore.value = fetchedData.length === size;
    }
  } catch (error) {
    if (error && error.code === 'UNAUTHORIZED') {
      loading.value = false;
      return;
    }
    console.error("Failed to fetch playlists:", error);
    // If we had cached data displayed, preserve it so offline users never get a blank screen
    if (!hadCachedData && playlists.value.length === 0) {
      playlists.value = [];
      hasMore.value = false;
    }
  } finally {
    loading.value = false;
    if (playlists.value.length > 0) {
      status.value = 'loaded';
    } else {
      status.value = 'empty';
    }
  }
}

const handleRefresh = async () => {
  if (isRefreshing.value) return;
  isRefreshing.value = true;
  const startTime = Date.now();
  try {
    // Invalida o cache local do dashboard para substituir por dados frescos da API
    PlaylistService.clearDashboardCache();

    // Notifica outros módulos/componentes do dashboard (como upcoming events na agenda)
    appEvents.emit('schedule:refresh');

    // Busca novos dados ignorando o cache
    await fetchPlaylists(true);

    const elapsed = Date.now() - startTime;
    if (elapsed < 450) {
      await new Promise(resolve => setTimeout(resolve, 450 - elapsed));
    }

    toastStore.dispatch('addMessage', {
      message: t('dashboard.dashboardRefreshed') || 'Dashboard atualizado com sucesso',
      icon: 'success'
    });
  } catch (err) {
    toastStore.dispatch('addMessage', {
      message: t('playlist.loadPlaylistsFailed') || 'Erro ao atualizar dados do dashboard',
      icon: 'error'
    });
  } finally {
    isRefreshing.value = false;
  }
}

const loadMorePlaylists = async () => {
  if (loadingMore.value || !hasMore.value) return;
  loadingMore.value = true;
  try {
    const nextPage = page.value + 1;
    const res = await PlaylistService.fetchPaginated({ page: nextPage, size: size });
    const fetchedData = res?.data || [];
    
    if (fetchedData.length > 0) {
      fetchedData.forEach(p => {
        p.isFavorite = isFavoritePlaylist(p);
      });

      playlists.value = deduplicatePlaylists([...playlists.value, ...fetchedData]);
      page.value = nextPage;
    }
    
    const totalPages = res?.totalPages || 0;
    if (totalPages > 0) {
      hasMore.value = page.value < totalPages;
    } else {
      hasMore.value = fetchedData.length === size;
    }
  } catch (error) {
    console.error("Failed to fetch more playlists:", error);
    hasMore.value = false;
  } finally {
    loadingMore.value = false;
  }
}

let lastLoadedUserId = null;
watch(
  () => userStore.state.userInfo?.id,
  async (newId) => {
    if (newId && newId !== lastLoadedUserId) {
      lastLoadedUserId = newId;
      playlists.value = [];
      favoritePlaylists.value = [];
      favoriteIds.value = new Set();
      await fetchPlaylists();
    }
  }
);

onMounted(async () => {
  applyCachedPlaylists();
  lastLoadedUserId = userStore.state.userInfo?.id || null;
  appEvents.on('app:online', fetchPlaylists);
  appEvents.on('offline:registry-updated', fetchPlaylists);
  appEvents.on('playlist:refresh-list', fetchPlaylists);
  await fetchPlaylists();
})

onUnmounted(() => {
  appEvents.off('app:online', fetchPlaylists);
  appEvents.off('offline:registry-updated', fetchPlaylists);
  appEvents.off('playlist:refresh-list', fetchPlaylists);
})
</script>